import axios, { AxiosInstance, AxiosError } from 'axios';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://devapi.edunxtai.info/api';

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Request interceptor - Add auth token to requests
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle errors globally
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Unauthorized - clear token and redirect to login
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// API Service
export const api = {
  // Authentication
  auth: {
    login: (email: string, password: string) =>
      apiClient.post('/admin/auth/login', { email, password }),
    logout: () => apiClient.post('/admin/auth/logout'),
    getProfile: () => apiClient.get('/admin/auth/me'),
    changePassword: (currentPassword: string, newPassword: string) =>
      apiClient.post('/admin/auth/change-password', { currentPassword, newPassword }),
    createAdmin: (data: any) => apiClient.post('/admin/auth/create-admin', data),
  },

  // Questions
  questions: {
    getAll: (params?: any) => apiClient.get('/admin/questions', { params }),
    getById: (id: number) => apiClient.get(`/admin/questions/${id}`),
    create: (data: any) => apiClient.post('/admin/questions', data),
    update: (id: number, data: any) => apiClient.put(`/admin/questions/${id}`, data),
    delete: (id: number) => apiClient.delete(`/admin/questions/${id}`),
    getHistory: (id: number) => apiClient.get(`/admin/questions/${id}/history`),

    // Verification
    getReviewQueue: (params?: any) => apiClient.get('/admin/questions/review/queue', { params }),
    getVerificationStats: () => apiClient.get('/admin/questions/review/stats'),
    getAllPendingIds: (params?: any) => apiClient.get('/admin/questions/review/all-ids', { params }),
    approve: (id: number, notes?: string) =>
      apiClient.post(`/admin/questions/${id}/approve`, { notes }),
    reject: (id: number, notes: string) =>
      apiClient.post(`/admin/questions/${id}/reject`, { notes }),
    submitForReview: (id: number) =>
      apiClient.post(`/admin/questions/${id}/submit-for-review`),
    bulkApprove: (question_ids: number[], notes?: string) =>
      apiClient.post('/admin/questions/bulk/approve', { question_ids, notes }),

    // Import/Export
    import: (boardId: number, file: File, mediumId?: number) => {
      const formData = new FormData();
      formData.append('board_id', boardId.toString());
      if (mediumId) {
        formData.append('medium_id', mediumId.toString());
      }
      formData.append('file', file);
      return apiClient.post('/admin/questions/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    export: (params: any) =>
      apiClient.get('/admin/questions/export', {
        params,
        responseType: 'blob',
      }),
    downloadSample: (format: 'A' | 'B' = 'B') =>
      apiClient.get('/admin/questions/sample-csv', {
        params: { format },
        responseType: 'blob',
      }),
  },

  // Exams
  exams: {
    getAll: (params?: any) => apiClient.get('/admin/exams', { params }),
    getById: (id: number) => apiClient.get(`/admin/exams/${id}`),
    create: (data: any) => apiClient.post('/admin/exams', data),
    update: (id: number, data: any) => apiClient.put(`/admin/exams/${id}`, data),
    delete: (id: number) => apiClient.delete(`/admin/exams/${id}`),
    toggleActive: (id: number) => apiClient.patch(`/admin/exams/${id}/toggle-active`),
    getStats: () => apiClient.get('/admin/exams/stats/dashboard'),
  },

  // Users
  users: {
    getParents: (params?: any) => apiClient.get('/admin/users/parents', { params }),
    getParentById: (id: number) => apiClient.get(`/admin/users/parents/${id}`),
    getStudents: (params?: any) => apiClient.get('/admin/users/students', { params }),
    getStudentById: (id: number) => apiClient.get(`/admin/users/students/${id}`),
    getPayments: (params?: any) => apiClient.get('/admin/users/payments', { params }),
    getStats: () => apiClient.get('/admin/users/stats'),
  },

  // Analytics
  analytics: {
    getDashboard: () => apiClient.get('/admin/analytics/dashboard'),
    getRevenue: (params?: any) => apiClient.get('/admin/analytics/revenue', { params }),
    getExamPerformance: (params?: any) =>
      apiClient.get('/admin/analytics/exam-performance', { params }),
    getQuestionUsage: (params?: any) =>
      apiClient.get('/admin/analytics/question-usage', { params }),
    getActivityLogs: (params?: any) =>
      apiClient.get('/admin/analytics/activity-logs', { params }),
  },

  // Master Data
  master: {
    getCountries: (withBoards?: boolean) =>
      apiClient.get('/master/countries', {
        params: withBoards ? { with_boards: 'true' } : {},
      }),
    getBoards: (countryId?: number) =>
      apiClient.get('/master/boards', {
        params: countryId ? { country_id: countryId } : {},
      }),
    getStandards: (boardId?: number) =>
      apiClient.get('/master/standards', { params: { board_id: boardId } }),
    getStreams: (standardId?: number) =>
      apiClient.get('/master/streams', { params: { standard_id: standardId } }),
    getSubjects: (boardId?: number, standardId?: number, mediumId?: number) =>
      apiClient.get('/master/subjects', {
        params: { board_id: boardId, standard_id: standardId, medium_id: mediumId },
      }),
    createSubject: (data: any) => apiClient.post('/admin/master/subjects', data),
    updateSubject: (id: number, data: any) => apiClient.put(`/admin/master/subjects/${id}`, data),
    deleteSubject: (id: number) => apiClient.delete(`/admin/master/subjects/${id}`),
    getMediums: () => apiClient.get('/master/mediums'),
    getChapters: (subjectId?: number) =>
      apiClient.get('/master/chapters', { params: { subject_id: subjectId } }),
    getTopics: (chapterId?: number) =>
      apiClient.get('/master/topics', { params: { chapter_id: chapterId } }),
    getSubtopics: (topicId?: number) =>
      apiClient.get('/master/subtopics', { params: { topic_id: topicId } }),
    getLanguages: () => apiClient.get('/master/languages'),
  },

  // Referral Codes
  referralCodes: {
    getAll: (params?: any) => apiClient.get('/admin/referrals', { params }),
    getById: (id: number) => apiClient.get(`/admin/referrals/${id}`),
    create: (data: any) => apiClient.post('/admin/referrals', data),
    update: (id: number, data: any) => apiClient.put(`/admin/referrals/${id}`, data),
    deactivate: (id: number) => apiClient.delete(`/admin/referrals/${id}`),
    getUsage: (id: number, params?: any) => apiClient.get(`/admin/referrals/${id}/usage`, { params }),
    getStats: (id: number) => apiClient.get(`/admin/referrals/${id}/stats`),
  },

  // Question Reports
  questionReports: {
    getAll: (params?: any) => apiClient.get('/admin/question-reports', { params }),
    getById: (id: number) => apiClient.get(`/admin/question-reports/${id}`),
    getStatistics: () => apiClient.get('/admin/question-reports/statistics'),
    getByQuestionId: (questionId: number) =>
      apiClient.get(`/admin/question-reports/question/${questionId}`),
    updateStatus: (id: number, status: string, adminNotes?: string) =>
      apiClient.put(`/admin/question-reports/${id}`, { status, admin_notes: adminNotes }),
    bulkUpdate: (reportIds: number[], status: string, adminNotes?: string) =>
      apiClient.put('/admin/question-reports/bulk-update', {
        report_ids: reportIds,
        status,
        admin_notes: adminNotes,
      }),
  },
};

export default api;
